import * as React from "react";
import { Lightbulb } from "lucide-react";

import { cn } from "../utils";

/**
 * Full-bleed footer strip — promoted from the `/styleguide` workflow prototypes
 * (`app/styleguide/_card-strip.tsx`), where both workflow detail pages built on
 * it (Matt: "we need to make this dry"). The workflow detail page
 * (`/agents/workflows/<slug>`) is the first product consumer.
 *
 * A strip is the last block in a card, and it is a SECTION, not a trailing
 * sentence: its top border runs the full width of the card (not inset to the
 * content gutter) and it sits on a faint wash, one perceptible step off the card
 * fill, so the block reads as defined. Three things it is used for so far —
 * the lightbulb footnote (`Hint`), a mini stat bar (`MiniStatStrip`), and any
 * one-off run of facts (`CardStrip` directly).
 *
 * It bleeds by cancelling the padding of whatever contains it, so the caller
 * names that frame. `loose` is the not-in-a-card case: a self-contained
 * bordered note on the page background.
 */
export type StripFrame = "p5" | "px5py3" | "px6py4" | "px6" | "px4py3" | "flush" | "loose";

const FRAME: Record<StripFrame, string> = {
  p5: "-mx-5 -mb-5 px-5",
  px5py3: "-mx-5 -mb-3 px-5",
  px6py4: "-mx-6 -mb-4 px-6",
  /** A px-6 card, but NOT the last block — bleed the sides, leave the bottom
   *  padding alone so a second strip can stack flush beneath it. */
  px6: "-mx-6 px-6",
  px4py3: "-mx-4 -mb-3 px-4",
  /** The container is already edge-to-edge (a `py-0` card) — supply the gutter, bleed nothing. */
  flush: "px-5",
  /**
   * Not inside a card — a self-contained note sitting directly in a `px-6` page
   * column. It still obeys the column's one outer edge: bleed to it (`-mx-6`)
   * and re-supply the gutter (`px-6`) so the note's text lands back on the text
   * rail. A `px-4` here put the text 8px left of the rail and the border 24px
   * inside every card above it.
   */
  loose: "-mx-6 rounded-md border border-border/60 px-6",
};

export function CardStrip({
  frame = "p5",
  className,
  children,
}: {
  frame?: StripFrame;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        // The fill is a WASH over whatever it sits on (`foreground` at 4%), not a
        // rung of the surface ladder: surface-2 is only 3/255 off the card in
        // dark mode, which is too faint to define a block. A wash steps away
        // from the resting fill by the same perceptible amount in both themes —
        // lighter on the near-black card, a faint gray on the white one.
        "mt-auto bg-foreground/[0.04] py-3",
        frame !== "loose" && "border-t border-border/60",
        FRAME[frame],
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Explanatory footnote — the lightbulb, as a defined strip across the bottom. */
export function Hint({ frame, children }: { frame?: StripFrame; children: React.ReactNode }) {
  return (
    <CardStrip frame={frame}>
      <p className="flex items-start gap-2 type-subtext text-muted-foreground">
        <Lightbulb className="mt-px size-3.5 shrink-0 text-amber-400/80" />
        <span>{children}</span>
      </p>
    </CardStrip>
  );
}

export type MiniStat = {
  label: string;
  /** The headline fact, e.g. "478 runs · $2.49/run". */
  value: React.ReactNode;
  /** The second, quieter line, e.g. "12m 3s avg · 26.6k tok". */
  sub?: React.ReactNode;
};

const COLUMNS: Record<2 | 3 | 4, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

/** A breakdown bar at the foot of a card — the same defined strip, holding a
 *  small grid of labelled numbers instead of prose. */
export function MiniStatStrip({
  stats,
  frame,
  columns,
  className,
}: {
  stats: readonly MiniStat[];
  frame?: StripFrame;
  /** Defaults to one column per stat (2–4), so a workflow with three roles fits. */
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const cols = columns ?? (Math.min(Math.max(stats.length, 2), 4) as 2 | 3 | 4);
  return (
    <CardStrip frame={frame} className={cn("grid gap-4", COLUMNS[cols], className)}>
      {stats.map((s) => (
        <div key={s.label} className="flex min-w-0 flex-col gap-0.5">
          {/* Rendered as authored — `capitalize` here title-cased every word,
              so a real label ("Cost / run") came out "Cost / Run". A caller
              with a lowercase datum (a role name) capitalizes it itself. */}
          <span className="truncate type-subtext text-muted-foreground">{s.label}</span>
          <span className="type-body tabular-nums text-foreground">{s.value}</span>
          {s.sub && <span className="type-subtext tabular-nums text-muted-foreground">{s.sub}</span>}
        </div>
      ))}
    </CardStrip>
  );
}
